import type { VersionState } from './types.ts';
import { $guests, $customFields, guestActions, historyActions } from './state.ts';
import { storageManager } from './storage.ts';
import { $currentVersionId, versionActions } from './state.ts';
import { HISTORY_CONFIG } from './constants.ts';

/**
 * History-aware action wrapper
 * Wraps guest actions to automatically create history entries
 */

let historyDebounceTimeout: number | null = null;

// Helper to save history to storage with debouncing
async function saveHistoryToStorage(): Promise<void> {
  if (historyDebounceTimeout) {
    clearTimeout(historyDebounceTimeout);
  }

  historyDebounceTimeout = window.setTimeout(async () => {
    const currentVersionId = $currentVersionId.get();
    
    if (!currentVersionId) {
      return;
    }

    try {
      // Set auto-saving indicator to true
      versionActions.setAutoSaving(true);
      
      // Get current state and history
      const currentState: VersionState = {
        guests: $guests.get(),
        customFields: $customFields.get()
      };
      const historyState = await import('./state.ts').then(m => m.$historyState.get());
      
      // Update both state and history using the new method
      const updatedVersion = await storageManager.updateVersionStateAndHistory(
        currentVersionId,
        currentState,
        historyState.entries
      );
      
      versionActions.updateVersion(updatedVersion);
      
      // Set auto-saving indicator to false when done
      versionActions.setAutoSaving(false);
    } catch (error) {
      console.error('Failed to save to storage:', error);
      // Set auto-saving indicator to false even if there was an error
      versionActions.setAutoSaving(false);
    }
    
    historyDebounceTimeout = null;
  }, HISTORY_CONFIG.DEBOUNCE_MS);
}

// Create history-aware wrapper for guest actions
export const historyAwareGuestActions = {
  moveGuest: (guestId: number, newTable: number, newSeat: number): void => {
    const beforeState = historyActions.getCurrentState();

    // Find the guest to get their current position
    const guest = guestActions.findGuest(guestId);
    const guestName = guest?.fullName || `Guest ${guestId}`;

    if (guest && guest.table === newTable && guest.seat === newSeat) {
      return; // No change, skip history
    }
    
    // Execute the original action
    guestActions.moveGuest(guestId, newTable, newSeat);
    
    const afterState = historyActions.getCurrentState();
    
    // Create history entry
    historyActions.addHistoryEntry(
      'guest_moved',
      `Moved ${guestName} to Table ${newTable}, Seat ${newSeat}`,
      beforeState,
      afterState,
      guestId
    );
    
    // Save to storage
    saveHistoryToStorage();
  },

  unplaceGuest: (guestId: number): void => {
    const beforeState = historyActions.getCurrentState();
    
    const guest = guestActions.findGuest(guestId);
    const guestName = guest?.fullName || `Guest ${guestId}`;
    
    guestActions.unplaceGuest(guestId);
    
    const afterState = historyActions.getCurrentState();
    
    historyActions.addHistoryEntry(
      'guest_unplaced',
      `Unplaced ${guestName}`,
      beforeState,
      afterState,
      guestId
    );
    
    saveHistoryToStorage();
  },

  updateGuest: (guestId: number, updates: Parameters<typeof guestActions.updateGuest>[1]): void => {
    const beforeState = historyActions.getCurrentState();
    
    const guest = guestActions.findGuest(guestId);
    const guestName = guest?.fullName || `Guest ${guestId}`;
    
    guestActions.updateGuest(guestId, updates);
    
    const afterState = historyActions.getCurrentState();
    
    // Create more specific description based on what was updated
    let description = `Updated ${guestName}`;
    if (updates.fullName) description = `Renamed guest to ${updates.fullName}`;
    if (updates.table && updates.seat) description = `Moved ${guestName} to Table ${updates.table}, Seat ${updates.seat}`;
    
    historyActions.addHistoryEntry(
      'guest_updated',
      description,
      beforeState,
      afterState,
      guestId
    );
    
    saveHistoryToStorage();
  },

  createGuest: (fullName: string, table?: number, seat?: number, customField1?: string, customField2?: string) => {
    const beforeState = historyActions.getCurrentState();
    
    const newGuest = guestActions.createGuest(fullName, table, seat, customField1, customField2);
    
    const afterState = historyActions.getCurrentState();
    
    let description = `Added ${fullName}`;
    if (table && seat) {
      description += ` at Table ${table}, Seat ${seat}`;
    } else {
      description += ` (unplaced)`;
    }
    
    historyActions.addHistoryEntry(
      'guest_added',
      description,
      beforeState,
      afterState,
      newGuest.id
    );
    
    saveHistoryToStorage();
    return newGuest;
  },

  // Bulk operations for CSV import
  importGuests: (guests: Array<{ fullName: string, table?: number, seat?: number, customField1?: string, customField2?: string }>, description: string = 'CSV Import'): void => {
    const beforeState = historyActions.getCurrentState();
    
    // Generate new guests with proper IDs
    const newGuests = guests.map((guestData, index) => {
      // Generate unique ID
      const newId = Date.now() + index + Math.random();
      return {
        id: newId,
        fullName: guestData.fullName.trim(),
        table: guestData.table || null,
        seat: guestData.seat || null,
        customField1: guestData.customField1 || undefined,
        customField2: guestData.customField2 || undefined
      };
    });
    
    // Replace all guests with imported ones
    $guests.set(newGuests);
    
    const afterState = historyActions.getCurrentState();
    
    historyActions.addHistoryEntry(
      'csv_import',
      `${description} (${guests.length} guests)`,
      beforeState,
      afterState
    );
    
    saveHistoryToStorage();
  },

  // Update custom fields
  updateCustomFields: (newCustomFields: Parameters<typeof $customFields.set>[0]): void => {
    const beforeState = historyActions.getCurrentState();
    
    $customFields.set(newCustomFields);
    
    const afterState = historyActions.getCurrentState();
    
    historyActions.addHistoryEntry(
      'custom_fields_updated',
      'Updated custom field settings',
      beforeState,
      afterState
    );
    
    saveHistoryToStorage();
  }
};

// Undo/Redo functions that restore state without creating new history
export const historyNavigation = {
  undo: (): boolean => {
    const previousState = historyActions.undo();
    
    if (previousState) {
      // Restore state without triggering history
      $guests.set(previousState.guests);
      $customFields.set(previousState.customFields);
      
      // Save to storage
      saveHistoryToStorage();
      
      console.log('Undid action, restored to previous state');
      return true;
    }
    
    return false;
  },

  redo: (): boolean => {
    const nextState = historyActions.redo();
    
    if (nextState) {
      // Restore state without triggering history
      $guests.set(nextState.guests);
      $customFields.set(nextState.customFields);
      
      // Save to storage
      saveHistoryToStorage();
      
      console.log('Redid action, restored to next state');
      return true;
    }
    
    return false;
  },

  // Load history when switching versions
  loadVersionHistory: (history: Array<any>): void => {
    historyActions.loadHistory(history);
  },

  // Clear history when switching versions
  clearHistory: (): void => {
    historyActions.clearHistory();
  }
};